import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import StarRatingComponent from 'react-star-rating-component';
import ImageHeader from '../../elements/ImageHeader';
import Text from '../../elements/Text';
import Box from '../../elements/Box';
import Icon from '../../elements/Icon';
import Separator from '../_commons/atoms/Separator';
import FareItemOriginDestination from '../_commons/atoms/FareItemOriginDestination';
import FareItemPrice from '../_commons/atoms/FareItemPrice';
import FareItemCallToAction from '../_commons/atoms/FareItemCallToAction';
import { handleFareItemInteraction } from '../_commons/commonFns';
import { usePlural } from '@front10/utils';

const Price = (props) => {
  const {
    fareItemPricePerNight,
    fareItemPriceTotal,
    data: { formattedPricePerNight: pricePerNight, formattedTotalPrice: totalPrice },
    showPricePerNight,
    showPriceTotal
  } = props;
  const { translate } = useContext(Context);
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="end"
      paddingBottom="1"
      style={{ lineHeight: 1 }}
    >
      {showPricePerNight && pricePerNight && (
        <Box display="flex" alignItems="center" fontName="AirbnbCerealBold">
          <FareItemPrice {...fareItemPricePerNight} formattedTotalPrice={pricePerNight} />
          <Separator content="/" paddingLeft="0" paddingRight="0" />
          <Text textTransform="lowercase">{translate('cmp-price-per-night-text', 'Night')}</Text>
        </Box>
      )}
      {showPriceTotal && totalPrice && (
        <Box display="flex" alignItems="center">
          <FareItemPrice {...fareItemPriceTotal} formattedTotalPrice={totalPrice} />
          <Text textTransform="lowercase" marginLeft="1" {...fareItemPriceTotal}>
            {translate('cmp-price-total-text', 'total')}
          </Text>
        </Box>
      )}
    </Box>
  );
};

Price.propTypes = {
  fareItemPricePerNight: PropTypes.objectOf(PropTypes.any),
  fareItemPriceTotal: PropTypes.objectOf(PropTypes.any),
  showPricePerNight: PropTypes.bool,
  showPriceTotal: PropTypes.bool,
  data: PropTypes.objectOf(PropTypes.any)
};

Price.defaultProps = {
  fareItemPricePerNight: {},
  fareItemPriceTotal: {},
  showPricePerNight: true,
  showPriceTotal: true,
  data: {}
};

const Stars = (props) => {
  const {
    reviewItemStyle,
    reviewQtyStyle,
    hostCategoryStyle,
    starRatingStyle,
    data: { reviewsQty, popularity, travelClass }
  } = props;

  const hostCategory = travelClass; // TODO
  return (
    <Box
      display="flex"
      flexDirection="row"
      paddingTop="1"
      style={{ lineHeight: 1 }}
      {...reviewItemStyle}
    >
      <StarRatingComponent
        editing={false}
        name="rate1"
        starCount={5}
        value={parseFloat(popularity)}
        renderStarIcon={(index, value) => {
          return (
            <Text {...starRatingStyle}>
              <Icon
                icon={index <= value ? 'star-full' : 'star-empty'}
                iconColor="white"
                size={12}
              />
            </Text>
          );
        }}
        renderStarIconHalf={() => {
          return (
            <Text {...starRatingStyle}>
              <Icon icon="star-half" iconColor="white" size={12} />
            </Text>
          );
        }}
      />
      <Text {...reviewQtyStyle}>{` ${reviewsQty} `}</Text>
      {hostCategory && <Text>·</Text>}
      <Text {...hostCategoryStyle}>{` ${hostCategory} `}</Text>
    </Box>
  );
};

Stars.propTypes = {
  reviewItemStyle: PropTypes.objectOf(PropTypes.any),
  reviewQtyStyle: PropTypes.objectOf(PropTypes.any),
  starRatingStyle: PropTypes.objectOf(PropTypes.any),
  hostCategoryStyle: PropTypes.objectOf(PropTypes.any),
  data: PropTypes.objectOf(PropTypes.any)
};

Stars.defaultProps = {
  reviewItemStyle: {},
  reviewQtyStyle: {},
  starRatingStyle: {},
  hostCategoryStyle: {},
  data: {}
};

const Capacity = (props) => {
  const {
    fareItemCapacity,
    data: { bedrooms, bathrooms, beds, guests }
  } = props;
  const translate = usePlural();
  return (
    <Box display="flex" style={{ lineHeight: 1 }} {...fareItemCapacity}>
      {guests && (
        <Box>
          <Text floatLeft>{translate({ key: 'cmp-airbnb-item-guests', count: guests })}</Text>
        </Box>
      )}
      {bedrooms && (
        <>
          <Text marginWidth="1">·</Text>
          <Box>
            <Text floatLeft>{translate({ key: 'cmp-airbnb-item-bedrooms', count: bedrooms })}</Text>
          </Box>
        </>
      )}
      {beds && (
        <>
          <Text marginWidth="1">·</Text>
          <Box>
            <Text floatLeft>{translate({ key: 'cmp-airbnb-item-beds', count: beds })}</Text>
          </Box>
        </>
      )}
      {bathrooms && (
        <>
          <Text marginWidth="1">·</Text>
          <Box>
            <Text floatLeft>{translate({ key: 'cmp-airbnb-item-baths', count: bathrooms })}</Text>
          </Box>
        </>
      )}
    </Box>
  );
};

Capacity.propTypes = {
  fareItemCapacity: PropTypes.objectOf(PropTypes.any),
  data: PropTypes.objectOf(PropTypes.any)
};

Capacity.defaultProps = {
  fareItemCapacity: {},
  data: {}
};

const ItemCard = (props) => {
  const {
    fareItemCard,
    fareItemOriginDestination,
    titlePlaceSettings,
    fareItemCallToAction,
    priceCallToActionItemStyles,
    data,
    data: { placeDescription: titlePlace },
    showCapacity
  } = props;
  return (
    <Box display="flex" justifyContent="between" {...fareItemCard}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="end"
        rtlReverse
        {...fareItemCard.fareBodyItem}
      >
        <FareItemOriginDestination {...fareItemOriginDestination} {...data} />
        {titlePlace && (
          <Text {...titlePlaceSettings} rtlReverse>
            {titlePlace}
          </Text>
        )}
        <Stars {...props} />
        {showCapacity && <Capacity {...props} />}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="end"
        {...priceCallToActionItemStyles}
      >
        <Price {...props} />
        <FareItemCallToAction {...fareItemCallToAction} />
      </Box>
    </Box>
  );
};

ItemCard.propTypes = {
  fareItemCapacity: PropTypes.objectOf(PropTypes.any),
  fareItemCard: PropTypes.objectOf(PropTypes.any),
  fareItemOriginDestination: PropTypes.objectOf(PropTypes.any),
  titlePlaceSettings: PropTypes.objectOf(PropTypes.any),
  fareItemCallToAction: PropTypes.objectOf(PropTypes.any),
  priceCallToActionItemStyles: PropTypes.objectOf(PropTypes.any),
  showCapacity: PropTypes.bool,
  data: PropTypes.objectOf(PropTypes.any)
};

ItemCard.defaultProps = {
  fareItemCapacity: {},
  fareItemCard: {},
  fareItemOriginDestination: {},
  titlePlaceSettings: {},
  fareItemCallToAction: {},
  priceCallToActionItemStyles: {},
  showCapacity: true,
  data: {}
};

const FeaturedOfferCard = (props) => {
  const {
    imageProps,
    image,
    data,
    showImage,
    focusReferences,
    focusable,
    airbnbItemStyles,
    showLabelInside,
    featuredOfferCardContainer
  } = props;
  const { direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const { handleClick, handleItemKeyDown } = handleFareItemInteraction(props);

  return (
    <Box
      focusable={focusable}
      tabIndex={focusable ? 0 : -1}
      focusReferences={focusReferences}
      className={className}
      style={style}
      onKeyDown={handleItemKeyDown}
      onClick={handleClick}
      dataTestId="featuredOfferCardLayoutRow"
      {...featuredOfferCardContainer}
    >
      {showImage ? (
        <ImageHeader {...imageProps} image={image}>
          {showLabelInside && <ItemCard {...airbnbItemStyles} data={data} />}
        </ImageHeader>
      ) : (
        <>{showLabelInside && <ItemCard {...airbnbItemStyles} data={data} />}</>
      )}
      {!showLabelInside && <ItemCard {...airbnbItemStyles} data={data} />}
    </Box>
  );
};

FeaturedOfferCard.propTypes = {
  /**
   * Class name of header
   */
  className: PropTypes.string,
  /**
   * Css style applied to the component
   */
  style: PropTypes.objectOf(PropTypes.any),
  /**
   * Image of destination.
   */
  image: PropTypes.string,
  /**
   * image props.
   */
  imageProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Name of destination.
   */
  name: PropTypes.string,
  /**
   * iataCode of destination.
   */
  iataCode: PropTypes.string,
  /**
   * data to render by the item
   */
  data: PropTypes.objectOf(PropTypes.any),
  /**
   * Header props
   */
  header: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  interationHotZone: PropTypes.oneOf(['smart', 'whole', 'button']),
  /**
   * Determine if the image will be displayed in the component.
   */
  showImage: PropTypes.bool,
  /**
   * Show or hide the city name
   */
  showCityName: PropTypes.bool,
  /**
   * OnKeyDown function.
   */
  onKeyDown: PropTypes.func,
  /**
   * FocusReferences functions
   */
  focusReferences: PropTypes.func,
  /**
   * Turn off or on tabIndex in the FeaturedOfferCard
   */
  focusable: PropTypes.bool,
  /**
   * Settings of seeMore component
   */
  seeMore: PropTypes.objectOf(PropTypes.any),
  /**
   * Show or hide see more
   */
  showSeeMore: PropTypes.bool,
  /**
   * Site edition of the component
   */
  siteEdition: PropTypes.string,
  /**
   * Url base for component see more
   */
  seeMoreBasicUrl: PropTypes.string,
  /**
   * Traffix variants
   */
  traffixVariant: PropTypes.string,
  /**
   * OnClick event
   */
  onClick: PropTypes.func,
  /**
   * Call to action onClick event
   */
  callToActionOnClick: PropTypes.func,
  /**
   * Settings for the card
   */
  airbnbItemStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if show label insede the image
   */
  showLabelInside: PropTypes.bool,
  fareItemCard: PropTypes.objectOf(PropTypes.any),
  fareItemOriginDestination: PropTypes.objectOf(PropTypes.any),
  titlePlaceSettings: PropTypes.objectOf(PropTypes.any),
  fareItemCallToAction: PropTypes.objectOf(PropTypes.any),
  priceCallToActionItemStyles: PropTypes.objectOf(PropTypes.any),
  fareItemCapacity: PropTypes.objectOf(PropTypes.any),
  showCapacity: PropTypes.bool,
  featuredOfferCardContainer: PropTypes.objectOf(PropTypes.any)
};
FeaturedOfferCard.defaultProps = {
  className: '',
  style: null,
  image: '',
  name: '',
  iataCode: '',
  data: null,
  imageProps: {},
  header: {},
  interationHotZone: 'smart',
  onClick: (props) => props,
  callToActionOnClick: (props) => props,
  showImage: true,
  showCityName: true,
  onKeyDown: () => {},
  focusReferences: () => {},
  focusable: false,
  seeMore: {
    display: 'block',
    textAlign: 'center',
    textColor: 'dark',
    fontWeight: 'bold',
    padding: '2'
  },
  showSeeMore: true,
  siteEdition: null,
  seeMoreBasicUrl: null,
  traffixVariant: 'ci',
  showCapacity: true,
  airbnbItemStyles: {
    fareItemCapacity: {
      fontSize: '12px'
    }
  },
  showLabelInside: false,
  fareItemCard: {},
  fareItemOriginDestination: {},
  titlePlaceSettings: {},
  fareItemCallToAction: {},
  priceCallToActionItemStyles: {},
  fareItemCapacity: {},
  featuredOfferCardContainer: {}
};
FeaturedOfferCard.componentName = 'FeaturedOfferCard';

export default FeaturedOfferCard;
